/**
 * The 44px column at the end of a project row.
 *
 * Only entries that do something. Finishing a project is here rather than only
 * on the project page for the same reason the client list carries its status
 * toggle: you notice that a job is done while scanning the list, not after
 * opening the record. Editing is here for the opposite reason - a wrong name or
 * a moved deadline is spotted while scanning too, and the fix was three clicks
 * and a page load away when it existed at all.
 *
 * The dialogs are opened and held by this menu rather than handed up to the list
 * as another callback: the menu already owns a write (the status toggle), they
 * need nothing from the page around them, and their state has to outlive the
 * popover, which unmounts its own children the moment it closes.
 *
 * Duplicate follows Edit rather than sitting further down, because the two are
 * the entries that act on the record's CONTENT, and everything under them is
 * about the record's place in the operator's day. It is also the one entry here
 * that produces a row this menu is not attached to: the dialog navigates into
 * the copy, so the register the operator was scanning is left behind rather than
 * refreshed. That is deliberate - a copy nobody is shown is a copy that gets
 * made twice.
 *
 * Archive sits between the status toggle and the delete, and the position is the
 * argument: those three are the ways a project leaves the operator's day, in
 * increasing order of finality - finished but still listed, off the list but
 * intact, gone. The separator above Delete is what says the last one is not like
 * the other two.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArchiveRestore, ArchiveX, ArrowUpRight, CheckCircle2, Copy, Loader2,
  MoreHorizontal, Pencil, Pin, PinOff, RotateCcw, Trash2,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { updateProject, type Project } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";
import { ArchiveProjectDialog, useUnarchiveProject } from "./ProjectArchive";
import { DuplicateProjectModal } from "./DuplicateProjectModal";
import { EditProjectModal } from "./EditProjectModal";
import { usePinProject } from "./ProjectPin";

export function ProjectRowMenu({
  project,
  onChanged,
  onDelete,
  onPinChanged,
  onArchiveChanged,
}: {
  project: Project;
  onChanged: (next: Project) => void;
  onDelete: () => void;
  /**
   * The pin moved.
   *
   * Its own callback rather than `onChanged`, and for a reason that is the
   * mirror of the archive one below: the pin endpoints answer with the PIN and
   * not with the project, so there is no saved row to hand back - and what has
   * to happen next is not on this row at all, it is the shortlist strip above
   * the register, which has to ask the server again. The list patches its own
   * row and bumps the strip.
   */
  onPinChanged: (pinned: boolean) => void;
  /**
   * Called after the project has crossed the archive line in either direction.
   *
   * Separate from `onChanged`, which patches the row in place, because whether
   * the row should still BE on screen is not a question this menu can answer:
   * it depends on the archive filter the register is currently under. Under the
   * default the row must leave; in an archive view it must stay and change its
   * badge. So the list is told that the set may have changed and decides for
   * itself - which also keeps the pager, the count and the portfolio strip
   * above it describing the same projects as the rows below.
   */
  onArchiveChanged: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const navigate = useNavigate();
  const pin = usePinProject();
  const [editing, setEditing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const unarchive = useUnarchiveProject();
  const done = project.status === "completed";
  const archived = !!project.archivedAt;

  async function toggleDone() {
    close();
    try {
      onChanged(await updateProject(project.id, { status: done ? "in_progress" : "completed" }));
    } catch {
      toast(t("common.error"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            navigate(`/projects/${project.id}`);
          }}
        >
          <ArrowUpRight className="size-4" />
          {t("projects.open")}
        </button>
        {/* Straight after Open, because pinning is the thing an operator does
            WHILE scanning - "I will need this one again later" is a thought that
            happens in the list, not on the project page. One entry and two
            directions, the same shape as the archive entry further down. */}
        <button
          type="button"
          className="crm-mi"
          disabled={pin.busy}
          onClick={() => {
            close();
            void pin.run(project, onPinChanged);
          }}
        >
          {pin.busy ? (
            <Loader2 className="size-4 animate-spin" />
          ) : project.pinned ? (
            <PinOff className="size-4" />
          ) : (
            <Pin className="size-4" />
          )}
          {t(project.pinned ? "projects.unpin" : "projects.pin")}
        </button>
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            setEditing(true);
          }}
        >
          <Pencil className="size-4" />
          {t("projects.edit")}
        </button>
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            setDuplicating(true);
          }}
        >
          <Copy className="size-4" />
          {t("projects.duplicate")}
        </button>
        <button type="button" className="crm-mi" onClick={() => void toggleDone()}>
          {done ? <RotateCcw className="size-4" /> : <CheckCircle2 className="size-4" />}
          {t(done ? "projects.reopen" : "projects.markCompleted")}
        </button>
        {/* One entry, two directions. A menu that showed both would offer the
            operator a choice between a state the project is already in and the
            one it is not, which is not a choice. */}
        {archived ? (
          <button
            type="button"
            className="crm-mi"
            disabled={unarchive.busy}
            onClick={() => {
              close();
              void unarchive.run(project, () => onArchiveChanged());
            }}
          >
            {unarchive.busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ArchiveRestore className="size-4" />
            )}
            {t("projects.unarchive")}
          </button>
        ) : (
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              close();
              setArchiving(true);
            }}
          >
            <ArchiveX className="size-4" />
            {t("projects.archive")}
          </button>
        )}
        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>

      {/* Outside the popover on purpose - see the note at the top of the file.
          The dialog portals itself to the body, so it takes no part in the
          row's layout from here. */}
      {editing ? (
        <EditProjectModal project={project} onClose={() => setEditing(false)} onSaved={onChanged} />
      ) : null}

      {/* No `onChanged` on the way out: the row this menu belongs to is
          unchanged by a copy, and what the register should do about the NEW
          project is not a question that can be answered from here - it depends
          on the filters the list is under. The operator is taken to the copy
          instead, which is where they wanted to be. */}
      {duplicating ? (
        <DuplicateProjectModal
          project={project}
          onClose={() => setDuplicating(false)}
          onCreated={(created) => {
            setDuplicating(false);
            navigate(`/projects/${created.id}`);
          }}
        />
      ) : null}

      {archiving ? (
        <ArchiveProjectDialog
          project={project}
          onClose={() => setArchiving(false)}
          onArchived={() => {
            setArchiving(false);
            onArchiveChanged();
          }}
        />
      ) : null}
    </span>
  );
}
